import React, { useEffect, useRef } from 'react';
import { Check, CheckCheck } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { router } from '@inertiajs/react';
import useThemeColors from '@/hooks/useThemeColors';

import type { Notification } from '@/types/notification';

interface NotificationDropdownProps {
    notifications: Notification[];
    onClose: () => void;
    onMarkAsRead: (id: number) => void;
    onMarkAllAsRead: () => void;
}

const NotificationDropdown: React.FC<NotificationDropdownProps> = ({
    notifications,
    onClose,
    onMarkAsRead,
    onMarkAllAsRead,
}) => {
    const { t } = useTranslation();
    const colors = useThemeColors();
    const dropdownRef = useRef<HTMLDivElement>(null);

    /**
     * Validate that URL is relative (internal) and safe to navigate to.
     * Prevents open redirect/phishing attacks.
     * @param url - URL to validate
     * @returns true if URL is safe (relative path starting with /)
     */
    const isSafeUrl = (url: string): boolean => {
        if (!url) return false;
        // Only allow relative URLs starting with /
        // Reject absolute URLs (http://, https://, //, etc.)
        return url.startsWith('/') && !url.startsWith('//');
    };

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                dropdownRef.current &&
                !dropdownRef.current.contains(event.target as Node)
            ) {
                onClose();
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () =>
            document.removeEventListener('mousedown', handleClickOutside);
    }, [onClose]);

    const formatDate = (dateString: string) => {
        const date = new Date(dateString);
        const now = new Date();
        const diff = now.getTime() - date.getTime();
        const minutes = Math.floor(diff / 60000);
        const hours = Math.floor(diff / 3600000);
        const days = Math.floor(diff / 86400000);

        if (minutes < 1) return t('notifications.timeAgo.justNow');
        if (minutes < 60)
            return t('notifications.timeAgo.minutesAgo', { count: minutes });
        if (hours < 24)
            return t('notifications.timeAgo.hoursAgo', { count: hours });
        if (days < 7)
            return t('notifications.timeAgo.daysAgo', { count: days });
        return date.toLocaleDateString();
    };

    return (
        <div
            ref={dropdownRef}
            className="absolute right-0 top-full mt-2 w-80 rounded-lg border border-gray-200 bg-white shadow-lg"
        >
            {/* Header */}
            <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3">
                <h3 className="font-semibold text-gray-900">
                    {t('notifications.title')}
                </h3>
                {notifications.length > 0 && (
                    <button
                        onClick={onMarkAllAsRead}
                        className={`flex items-center gap-1 text-sm ${colors.buttonSecondary}`}
                    >
                        <CheckCheck size={16} />
                        {t('notifications.markAllAsRead')}
                    </button>
                )}
            </div>

            {/* Notifications List */}
            <div className="max-h-96 overflow-y-auto">
                {notifications.length === 0 ? (
                    <div className="px-4 py-8 text-center text-gray-500">
                        {t('notifications.noNotifications')}
                    </div>
                ) : (
                    notifications.map((notification) => (
                        <div
                            key={notification.id}
                            className="border-b border-gray-100 hover:bg-gray-50"
                        >
                            <div className="flex items-start justify-between gap-2">
                                <div
                                    className={`flex-1 px-4 py-3 ${notification.url && isSafeUrl(notification.url) ? 'cursor-pointer' : ''}`}
                                    onClick={() => {
                                        if (notification.url && isSafeUrl(notification.url)) {
                                            onMarkAsRead(notification.id);
                                            router.visit(notification.url);
                                            onClose();
                                        }
                                    }}
                                >
                                    <p className="font-medium text-gray-900">
                                        {notification.title}
                                    </p>
                                    <p className="mt-1 text-sm text-gray-600">
                                        {notification.message}
                                    </p>
                                    <p className="mt-1 text-xs text-gray-400">
                                        {formatDate(notification.created_at)}
                                    </p>
                                </div>
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onMarkAsRead(notification.id);
                                    }}
                                    className="mr-4 mt-3 flex-shrink-0 rounded p-1 text-gray-400 hover:bg-gray-200 hover:text-gray-600"
                                    title={t('notifications.markAsRead')}
                                >
                                    <Check size={16} />
                                </button>
                            </div>
                        </div>
                    ))
                )}
            </div>
        </div>
    );
};

export default NotificationDropdown;
